import React from "react";
import { Filter, RotateCcw } from "lucide-react";

export const DEFAULT_PERMIT_FILTERS = {
  status: "all",
  type: "all",
  fromDate: "",
  toDate: "",
  contractor: "all",
};

export default function PermitFilters({
  filters,
  setFilters,
  templates = [],
  contractors = [],
  showDraftOption = false,
  onClear,
}) {
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const hasActiveFilter = Boolean(
    (filters.status && filters.status !== "all") ||
    (filters.type && filters.type !== "all") ||
    Boolean(filters.fromDate) ||
    Boolean(filters.toDate) ||
    (filters.contractor && filters.contractor !== "all")
  );

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else {
      setFilters(DEFAULT_PERMIT_FILTERS);
    }
  };

  return (
    <div className="mb-6 rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-2xs">
      <div className="mb-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <span>Filters</span>
          </div>
          {hasActiveFilter && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
              Active
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleClear}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
            hasActiveFilter
              ? "border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100"
              : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          }`}
          title="Reset all filters"
        >
          <RotateCcw className="h-3 w-3" />
          Clear Filters
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {/* 1. Status Filter */}
        <div>
          <label
            htmlFor="permit-filter-status"
            className="mb-1.5 block text-xs font-semibold text-slate-600"
          >
            Status
          </label>
          <select
            id="permit-filter-status"
            value={filters.status || "all"}
            onChange={(e) => updateFilter("status", e.target.value)}
            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm text-slate-800 outline-none transition-colors focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100 cursor-pointer"
          >
            <option value="all">All</option>
            {showDraftOption && <option value="draft">Draft</option>}
            <option value="issue_in_progress">Pending Issue</option>
            <option value="closure_in_progress">Pending Closure</option>
            <option value="issued">Issued</option>
            <option value="closed">Closed</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* 2. Type Filter */}
        <div>
          <label
            htmlFor="permit-filter-type"
            className="mb-1.5 block text-xs font-semibold text-slate-600"
          >
            Type
          </label>
          <select
            id="permit-filter-type"
            value={filters.type || "all"}
            onChange={(e) => updateFilter("type", e.target.value)}
            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm text-slate-800 outline-none transition-colors focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100 cursor-pointer"
          >
            <option value="all">All Types</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* 3. From Date Filter */}
        <div>
          <label
            htmlFor="permit-filter-from-date"
            className="mb-1.5 block text-xs font-semibold text-slate-600"
          >
            From Date
          </label>
          <input
            id="permit-filter-from-date"
            type="date"
            value={filters.fromDate || ""}
            max={filters.toDate || undefined}
            onChange={(e) => updateFilter("fromDate", e.target.value)}
            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm text-slate-800 outline-none transition-colors focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100 cursor-pointer"
          />
        </div>

        {/* 4. To Date Filter */}
        <div>
          <label
            htmlFor="permit-filter-to-date"
            className="mb-1.5 block text-xs font-semibold text-slate-600"
          >
            To Date
          </label>
          <input
            id="permit-filter-to-date"
            type="date"
            value={filters.toDate || ""}
            min={filters.fromDate || undefined}
            onChange={(e) => updateFilter("toDate", e.target.value)}
            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm text-slate-800 outline-none transition-colors focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100 cursor-pointer"
          />
        </div>

        {/* 5. Contractor Filter */}
        <div>
          <label
            htmlFor="permit-filter-contractor"
            className="mb-1.5 block text-xs font-semibold text-slate-600"
          >
            Contractor
          </label>
          {(() => {
            const internalOption = contractors.find((c) => c.value === "internal");
            const internalLabel = internalOption?.label || "Internal";
            return (
              <select
                id="permit-filter-contractor"
                value={filters.contractor || "all"}
                onChange={(e) => updateFilter("contractor", e.target.value)}
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm text-slate-800 outline-none transition-colors focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100 cursor-pointer"
              >
                <option value="all">All Contractors</option>
                <option value="internal">{internalLabel}</option>
                {contractors
                  .filter((c) => c.value !== "all" && c.value !== "internal")
                  .map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
              </select>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
